/**
 * Resolves which store the KDS opens (RF-004).
 *
 * Priority:
 *   1. `?merchantId=<uuid>` in the URL → use it (admin support path, RF-004).
 *   2. Otherwise call `GET /api/v1/merchants/me` and branch on membership
 *      count: 0 → no store (or admin selector); 1 → that store; >1 → selector.
 *
 * We resolve a concrete merchantId on the front because the WS topic
 * (`/topic/merchant.{id}.orders`) and the snapshot query key both need it.
 */
import { useQuery } from '@tanstack/react-query';
import { getMyMerchants } from '../api';
import type { MerchantMembership } from '../types';

export type MerchantResolution =
  | { state: 'loading' }
  | { state: 'error'; error: Error }
  /** Caller has no store and is not an admin (EC-018). */
  | { state: 'none' }
  /** Owner with >1 store, or admin without `?merchantId` (`adminSearch`). */
  | { state: 'select'; memberships: MerchantMembership[]; adminSearch: boolean }
  | { state: 'resolved'; merchantId: string; memberships: MerchantMembership[] };

export function useResolveMerchant(
  merchantIdFromUrl: string | null,
  isAdmin: boolean,
): MerchantResolution {
  // Always fetched (even with ?merchantId= in the URL): the memberships carry the
  // store STATUS, which the OwnerHome needs to gate activation (PENDING_OWNER_ACTIVATION).
  const query = useQuery<MerchantMembership[], Error>({
    queryKey: ['kds', 'my-merchants'],
    queryFn: getMyMerchants,
    retry: false,
    // 60s: mudanças de equipe/status aparecem sem F5 (era 5min e nunca invalidava).
    staleTime: 60_000,
  });

  if (merchantIdFromUrl) {
    return { state: 'resolved', merchantId: merchantIdFromUrl, memberships: query.data ?? [] };
  }
  if (query.isLoading) return { state: 'loading' };
  if (query.isError) return { state: 'error', error: query.error };

  const memberships = query.data ?? [];
  if (memberships.length === 0) {
    return isAdmin ? { state: 'select', memberships: [], adminSearch: true } : { state: 'none' };
  }
  const [first] = memberships;
  if (memberships.length === 1 && first) {
    return { state: 'resolved', merchantId: first.id, memberships };
  }
  return { state: 'select', memberships, adminSearch: false };
}
